<script setup lang="ts">
// ================================
// LE GRAPHIQUE DES PUISSANCES, VISIBLE AU REPOS (maquette finale du
// 15/10/2026, DEV-2404 ; R12, M12 ; forme D14 du lot 4c, 18/10/2026).
//
// Ce que le courtier doit vivre : sous la carte de tête, sans aucun clic, la
// carte « Puissances par poste » (BaseTitledCard) et son graphique pleine
// largeur ; seuls s'y ajoutent ce que le graphique ne peut pas dire (une courbe
// incomplète, l'option chiffrée sous le seuil, la période plus complète quand
// la carte ne la propose pas). La formule, le raccordement hors échelle et la
// règle de croissance se lisent dans « Pourquoi ces puissances » ; les mesures
// et les réserves dans « Points à vérifier ».
// ================================

// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import OptimTurpeV2Chiffre from "./OptimTurpeV2Chiffre.vue"
import OptimTurpeV2GraphiquePuissances from "./OptimTurpeV2GraphiquePuissances.vue"
import OptimTurpeV2TableauPlages from "./OptimTurpeV2TableauPlages.vue"
import {
    formatDateFr,
    getMeilleurePeriode,
    getNiveauDonnees,
    libelleNiveauCourbe,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    lireOptionSousLeSeuil,
    lireSolutionRetenue,
    lireStatutStructure,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { formatPctMesure } from "@modules/courtage/utils/optim-turpe-v2.qualite"
import { phraseDuMontant } from "@modules/courtage/utils/optim-turpe-v2.entetes"
import {
    libelleChiffrerSurLaPeriode,
    phraseEnteteGraphiqueC5,
} from "@modules/courtage/utils/optim-turpe-v2.carte"
import { estC5 } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { NIVEAU_2 } from "@modules/courtage/utils/optim-turpe-v2.boutons"
import {
    COLONNE_OPTION_CHIFFREE,
    colonneProposee,
    phraseOptionChiffree,
} from "@modules/courtage/utils/optim-turpe-v2.tableau"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2NiveauDonnees,
} from "@modules/courtage/types/optim-turpe-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            titre: "Puissances par poste",
            option: {
                titre: "Option chiffrée",
                valeur: "Option chiffrée",
            },
            // Lot 4c (D14, choix 12) : le tableau des valeurs, replié.
            valeurs: {
                voir: "Voir les valeurs",
                masquer: "Masquer les valeurs",
            },
            meilleurePeriode: {
                hint: "Période plus complète disponible :",
                dispo: "({pct} de données)",
            },
        },
    },
})

// ================================
// TYPES & PROPS
// ================================
type Props = {
    etude: OptimTurpeV2Etude
    /** La paire « sans / avec dépassements », pour le titre du résultat (D27). */
    sans?: OptimTurpeV2Etude | null
    avec?: OptimTurpeV2Etude | null
    /**
     * Sous la carte de tête (vague wording du 14/10/2026, Q17) : le chiffre
     * ne redit pas le montant, et la période plus complète est le bouton de
     * la carte.
     */
    sousLaCarte?: boolean
    /**
     * La carte de tête porte-t-elle « Chiffrer sur la période du … au … » ?
     * Le même geste n'est alors pas redit ici (série UX du témoin, 2.2).
     */
    periodeDansLaCarte?: boolean
    /**
     * Revue 2 en production (I24, I61, lot C) : le scénario affiché n'est pas
     * le recommandé ; le graphique garde sa colonne, « Option chiffrée ».
     */
    optionChiffree?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    sans: null,
    avec: null,
    sousLaCarte: false,
    periodeDansLaCarte: false,
    optionChiffree: false,
})

// ================================
// LE GRAPHIQUE ET SON TABLEAU REPLIÉ (lot 4c, D14)
// ================================
/** Le tableau des valeurs, replié au repos (choix 12) : il sert à copier les chiffres. */
const valeursOuvertes = ref(false)

/** Le scénario de l'étude affichée dans la paire, lu sur l'identifiant. */
const getScenario = computed<"sans" | "avec" | null>(() => {
    const id = props.etude._id
    if (!id) return null
    if (props.sans?._id === id) return "sans"
    if (props.avec?._id === id) return "avec"
    return null
})

/** L'autre étude de la paire : repère creux (cas 8) ou valeurs citées en pied (cas 3). */
const getAutre = computed<OptimTurpeV2Etude | null>(() => {
    if (getScenario.value === "sans") return props.avec
    if (getScenario.value === "avec") return props.sans
    return null
})

/** L'étiquette de l'entête (maquette D14) : « Recommandé », « Vos puissances » ou « Option chiffrée ». */
const getEtiquette = computed(() =>
    colonneProposee(props.etude, { optionChiffree: props.optionChiffree })
)

/** Demande de relance sur la période recommandée (la page la passe à `runBySource`). */
const emit = defineEmits<{
    "relancer-periode": [periode: { date_from: string; date_to: string }]
    /** Mener à la rangée « Dépassements » (D27). */
    choisir: []
}>()

// ================================
// LA COURBE, L'OPTION, LA PÉRIODE
// ================================
/**
 * La phrase du montant n'est mise en avant que quand la structure est
 * RECOMMANDÉE (statut servi) ; une option sous le seuil n'en est pas une
 * (DEV-2183).
 */
const getIsEconomie = computed(
    () => lireStatutStructure(props.etude) === "recommandee"
)

/** Le niveau de la courbe de charge, lu de `qualite_donnees` (DEV-783). */
const getNiveau = computed<OptimTurpeV2NiveauDonnees | undefined>(() =>
    getNiveauDonnees(props.etude)
)
/** Le libellé décrit la COURBE DE CHARGE, jamais une fiabilité (DEV-848). */
const getNiveauLabel = computed(() => libelleNiveauCourbe(props.etude) ?? "")
/** Une courbe incomplète se signale en orange, une inconnue en gris. */
const getNiveauSeverity = computed(() => {
    switch (getNiveau.value) {
        case "fiable":
            return "success"
        case "reserve":
        case "indicatif":
        case "faible":
            return "warn"
        default:
            return "secondary"
    }
})

/**
 * Sous « structure déjà optimale », une autre structure n'est jamais
 * « recommandée » (DEV-2183) : elle est une option chiffrée sous le seuil.
 */
const getOption = computed(() => lireOptionSousLeSeuil(props.etude))
/**
 * Revue 2 en production (I61, I87, lot C) : le texte de l'encadré est écrit
 * par le front, « par an », le seuil comparé au montant arrondi ; la phrase du
 * moteur n'est qu'un repli sans montant lisible.
 */
const getPhraseOption = computed(
    () => phraseOptionChiffree(props.etude) ?? getOption.value?.libelle ?? ""
)

/**
 * La jambe « avec dépassements » a retenu la structure de l'autre
 * (courtage#253). Jamais sur un C5 (D18, DEV-2423) : la phrase nomme un
 * scénario des dépassements.
 */
const getSolutionRetenue = computed(() =>
    estC5(props.etude) ? null : lireSolutionRetenue(props.etude)
)

/**
 * DEV-2423 : l'entête de la carte sur un C5 avec la réserve servie, « Le
 * compteur a atteint 9,0 kVA pour 6 kVA souscrits. » ; sans réserve, aucune
 * phrase de scénario.
 */
const getPhraseC5 = computed(() => phraseEnteteGraphiqueC5(props.etude))

/** Ce qui fait le montant, en une phrase lue sur les postes servis. */
const getPhrase = computed(() => phraseDuMontant(props.etude))

/** La période plus complète proposée par le moteur (DEV-762). */
const getMeilleurePeriodeValue = computed(() =>
    getMeilleurePeriode(props.etude)
)

/** « Chiffrer sur la période du … au … » : la page relance sur ces dates. */
function onRelancerPeriode(): void {
    const p = getMeilleurePeriodeValue.value
    if (p)
        emit("relancer-periode", { date_from: p.date_from, date_to: p.date_to })
}
</script>

<template>
    <!-- ================================ -->
    <!-- PUISSANCES PAR POSTE : la carte titrée du graphique, toujours ouverte. -->
    <!-- ================================ -->
    <BaseTitledCard
        class="optim-turpe-v2-conclusion"
        content-class="p-0"
        data-testid="optim-turpe-v2-conclusion"
    >
        <template #header>
            <div class="flex flex-wrap items-center justify-between gap-2">
                <h2 class="text-lg font-semibold text-primary-900">
                    {{ t("titre") }}
                </h2>
                <BaseTag
                    v-if="getEtiquette"
                    :severity="
                        getEtiquette === COLONNE_OPTION_CHIFFREE
                            ? 'secondary'
                            : 'primary'
                    "
                    :value="getEtiquette"
                    data-testid="optim-turpe-v2-conclusion-etiquette"
                />
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <div
                    class="flex flex-wrap items-start justify-between gap-x-6 gap-y-2"
                >
                    <div class="flex flex-col gap-1.5">
                        <!-- DEV-2423 : sur un C5 avec la réserve, le constat. -->
                        <p
                            v-if="getPhraseC5"
                            class="text-sm text-primary-800"
                            data-testid="optim-turpe-v2-conclusion-phrase-c5"
                        >
                            {{ getPhraseC5 }}
                        </p>
                        <!-- Ce que le montant de la carte ne dit pas (A3, A4). -->
                        <OptimTurpeV2Chiffre
                            :etude="etude"
                            :sans="sans"
                            :avec="avec"
                            :sous-la-carte="sousLaCarte"
                            @choisir="emit('choisir')"
                        />
                        <p
                            v-if="getSolutionRetenue"
                            class="text-sm text-surface-700"
                            data-testid="optim-turpe-v2-solution-retenue"
                        >
                            {{ getSolutionRetenue }}
                        </p>
                    </div>
                    <!-- Une courbe incomplète le dit en pastille. -->
                    <BaseTag
                        v-if="getNiveauLabel && getNiveau !== 'fiable'"
                        :severity="getNiveauSeverity"
                        :value="getNiveauLabel"
                    />
                </div>

                <!-- Le graphique D14, pleine largeur (lot 4c) ; la formule et
                     la règle de croissance restent dans « Pourquoi ces
                     puissances ». -->
                <OptimTurpeV2GraphiquePuissances
                    :etude="etude"
                    :option-chiffree="optionChiffree"
                    :autre="getAutre"
                    :scenario="getScenario"
                />
                <!-- Choix 12 : le tableau reste disponible, replié, pour
                     copier les chiffres ; il est la source des lecteurs
                     d'écran une fois ouvert. -->
                <div class="flex flex-col gap-2">
                    <BaseButton
                        :label="
                            valeursOuvertes
                                ? t('valeurs.masquer')
                                : t('valeurs.voir')
                        "
                        size="small"
                        text
                        class="self-start p-0!"
                        :aria-expanded="valeursOuvertes"
                        data-testid="optim-turpe-v2-voir-valeurs"
                        @click="valeursOuvertes = !valeursOuvertes"
                    />
                    <div
                        v-show="valeursOuvertes"
                        data-testid="optim-turpe-v2-valeurs"
                    >
                        <OptimTurpeV2TableauPlages
                            :etude="etude"
                            repli-des-notes
                            sans-graphique
                            :option-chiffree="optionChiffree"
                        />
                    </div>
                </div>

                <p
                    v-if="getIsEconomie && getPhrase && !sousLaCarte"
                    class="text-sm text-surface-700"
                    data-testid="optim-turpe-v2-phrase-montant"
                >
                    {{ getPhrase }}
                </p>

                <!-- Déjà optimale : l'autre structure est une option chiffrée
                     sous le seuil, jamais une recommandation (DEV-2183). -->
                <div
                    v-if="getOption"
                    class="flex flex-col gap-1 rounded-md bg-surface-50 px-3 py-2"
                    data-testid="optim-turpe-v2-option-sous-le-seuil"
                >
                    <p class="text-sm font-medium text-primary-700">
                        {{ t("option.titre") }}
                    </p>
                    <p
                        class="text-sm text-surface-800"
                        data-testid="optim-turpe-v2-option-phrase"
                    >
                        {{ getPhraseOption }}
                    </p>
                    <dl v-if="getOption.reserve" class="text-sm">
                        <div
                            class="flex min-w-0 flex-col"
                            data-testid="optim-turpe-v2-reserve-ligne"
                            :data-valeur="t('option.valeur')"
                        >
                            <dt class="sr-only">{{ t("option.valeur") }}</dt>
                            <dd class="text-[var(--p-orange-700)]">
                                {{ getOption.reserve }}
                            </dd>
                        </div>
                    </dl>
                </div>

                <!-- Meilleure période disponible (DEV-762), quand la carte ne
                     porte pas déjà le geste (2.2, Q26). -->
                <div
                    v-if="getMeilleurePeriodeValue && !periodeDansLaCarte"
                    class="flex flex-wrap items-center gap-2 rounded-md bg-surface-50 px-3 py-2 text-sm text-surface-700"
                >
                    <span>
                        {{ t("meilleurePeriode.hint") }}
                        <span class="font-medium"
                            >{{
                                formatDateFr(
                                    getMeilleurePeriodeValue.date_from
                                ) ?? getMeilleurePeriodeValue.date_from
                            }}
                            →
                            {{
                                formatDateFr(
                                    getMeilleurePeriodeValue.date_to
                                ) ?? getMeilleurePeriodeValue.date_to
                            }}</span
                        >
                        <template
                            v-if="
                                getMeilleurePeriodeValue.pct_available != null
                            "
                        >
                            {{
                                t("meilleurePeriode.dispo", {
                                    pct: formatPctMesure(
                                        getMeilleurePeriodeValue.pct_available
                                    ),
                                })
                            }}
                        </template>
                    </span>
                    <BaseButton
                        v-bind="NIVEAU_2"
                        :label="
                            libelleChiffrerSurLaPeriode({
                                date_from: getMeilleurePeriodeValue.date_from,
                                date_to: getMeilleurePeriodeValue.date_to,
                            })
                        "
                        icon="pi pi-refresh"
                        data-testid="optim-turpe-v2-conclusion-chiffrer-periode"
                        @click="onRelancerPeriode"
                    />
                </div>
            </div>
        </template>
    </BaseTitledCard>
</template>
